import type { SortOrder } from '@/data-viz/data-source.interface';
import type { BuildingId } from '@/interfaces/Building.interface';
import type { UnzonedDateTime } from '@/interfaces/Date.interface';
import type { SensorTimeseriesId } from '@/interfaces/Timeseries.interface';
import { UnitCode, UnitGroupCode } from '@/interfaces/Unit.interface';
import { APIError } from '@/providers/errors.constants';
import { InformationProvider } from '@/providers/information/information.provider';
import { UnitProvider } from '@/providers/unit/unit.provider';
import { AdvizeoModalService } from '@/services/advizeo-modal.service';
import { SharedUiColorNameV2 } from '@/shared/ui/theming';
import { PaginatedResource } from '@/shared/utils/angular-sdk';
import { ButtonStyle } from '@/ui/button/ui-button';
import { PaginatedResourceSignals } from '@/utils/angular/signals/paginated-resource-signals';
import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, type Observable, of, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import {
    type AlertScenarioId,
    AlertScenarioType,
    type AlertSortableColumn,
    type AlertStatPeriodCode,
    type AlertValidation
} from '../../alerts.interface';
import { AdvizeoCoreAlertScenariosService } from '../../services/advizeo-core-alert-scenarios.service';
import {
    MeasureTypeCode,
    type ThresholdAlertScenarioCreation,
    type ThresholdAlertScenariosFilters,
    type ThresholdAlertScenarioUpdate,
    type ThresholdScenario,
    type ThresholdScenarioAlertsTimeseriesAnalysis,
    type ThresholdScenarioGlobalSummary,
    type ThresholdScenarioSensorStat,
    type ThresholdScenarioStat,
    type ThresholdScenarioSummary
} from '../threshold-alert-scenario.interface';
import { AdvizeoCoreThresholdScenarioService } from './advizeo-core-threshold-scenario.service';

@Injectable({
    providedIn: 'root'
})
export class ThresholdScenarioService {
    private _advizeoCoreThresholdScenariosProvider = inject(AdvizeoCoreThresholdScenarioService);
    private _unitProvider = inject(UnitProvider);
    private _modalService = inject(AdvizeoModalService);
    private _informationProvider = inject(InformationProvider);
    private _translateService = inject(TranslateService);
    private _advizeoCoreAlertScenariosProvider = inject(AdvizeoCoreAlertScenariosService);

    private _alertsScenariosListUpdate$ = new Subject<void>();

    getAlertsScenarios(
        params: () => { periodCode: AlertStatPeriodCode; isProjectAdmin: boolean; filters: ThresholdAlertScenariosFilters }
    ): PaginatedResourceSignals<ThresholdScenarioSummary> {
        return new PaginatedResourceSignals(
            params,
            ({ periodCode, isProjectAdmin, filters }, page, pageSize) =>
                firstValueFrom(
                    this._advizeoCoreThresholdScenariosProvider.fetchAlertScenarios$(
                        { page, size: pageSize },
                        periodCode,
                        isProjectAdmin,
                        filters
                    )
                ),
            {
                dataChanged$: this._alertsScenariosListUpdate$
            }
        );
    }

    getAlertScenario$(alertScenarioId: AlertScenarioId): Observable<ThresholdScenario> {
        return this._advizeoCoreThresholdScenariosProvider.getAlertScenario$(alertScenarioId);
    }

    getAlertScenarioSummary$(alertScenarioId: AlertScenarioId, periodCode: AlertStatPeriodCode): Observable<ThresholdScenarioSummary> {
        return this._advizeoCoreThresholdScenariosProvider.getAlertScenarioSummary$(alertScenarioId, periodCode);
    }

    getAlertScenarioGlobalSummary$(periodCode: AlertStatPeriodCode): Observable<ThresholdScenarioGlobalSummary> {
        return this._advizeoCoreThresholdScenariosProvider.getAlertScenarioGlobalSummary$(periodCode);
    }

    getAlertScenarioBuildingsStats(alertScenarioId: AlertScenarioId): PaginatedResource<
        ThresholdScenarioStat,
        {
            periodCode: AlertStatPeriodCode;
            search: string;
            sort?:
                | {
                      name: AlertSortableColumn;
                      direction: SortOrder;
                  }[]
                | null;
        }
    > {
        return new PaginatedResource(({ periodCode, search, sort }, page, pageSize) =>
            firstValueFrom(
                this._advizeoCoreThresholdScenariosProvider.fetchAlertScenarioBuildingsStats$(
                    { page, size: pageSize, sort },
                    alertScenarioId,
                    periodCode,
                    search
                )
            )
        );
    }

    getAlertScenarioSensorsStats$(
        alertScenarioId: AlertScenarioId,
        buildingId: BuildingId,
        startDate: UnzonedDateTime,
        endDate: UnzonedDateTime
    ): Observable<ThresholdScenarioSensorStat[]> {
        return this._advizeoCoreThresholdScenariosProvider.fetchAlertScenarioSensorStats$(alertScenarioId, buildingId, startDate, endDate);
    }

    getScenarioValidation$(alertScenarioId: AlertScenarioId): Observable<AlertValidation> {
        return this._advizeoCoreThresholdScenariosProvider.alertScenarioValidation$(alertScenarioId);
    }

    createThresholdAlertScenario(thresholdScenarioValue: ThresholdAlertScenarioCreation): Promise<AlertScenarioId> {
        return this._advizeoCoreThresholdScenariosProvider.createThresholdAlertScenario(thresholdScenarioValue);
    }

    updateThresholdAlertScenario(
        alertScenarioId: AlertScenarioId,
        thresholdAlertScenarioUpdate: ThresholdAlertScenarioUpdate
    ): Promise<void> {
        return this._advizeoCoreThresholdScenariosProvider.updateThresholdAlertScenario(alertScenarioId, thresholdAlertScenarioUpdate);
    }

    saveAlertScenario(alertScenarioId: AlertScenarioId): Promise<void> {
        return this._advizeoCoreThresholdScenariosProvider.saveAlertScenario(alertScenarioId);
    }

    publishAlertScenario(alertScenarioId: AlertScenarioId): Promise<void> {
        return this._advizeoCoreThresholdScenariosProvider.publishAlertScenario(alertScenarioId);
    }

    requestAlertScenarioDeletion(alertScenarioId: AlertScenarioId): Promise<void> {
        return this._modalService
            .openDialog({
                title: this._translateService.instant('PAGES.ALERT_SCENARIO.DELETE_SCENARIO.TITLE'),
                message: this._translateService.instant('PAGES.ALERT_SCENARIO.DELETE_SCENARIO.DESCRIPTION'),
                buttons: [
                    {
                        text: this._translateService.instant('BUTTONS.VALIDATE'),
                        onClick: () => {
                            this.deleteAlertScenario(alertScenarioId)
                                .then(() => {
                                    this._informationProvider.showToast(
                                        this._translateService.instant('PAGES.ALERT_SCENARIO.DELETE_SCENARIO.SUCCESS'),
                                        'success'
                                    );
                                })
                                .catch(() => {
                                    this._informationProvider.showToast(
                                        this._translateService.instant('PAGES.ALERT_SCENARIO.DELETE_SCENARIO.ERROR'),
                                        'error'
                                    );
                                });
                        },
                        color: SharedUiColorNameV2.PRIMARY
                    },
                    {
                        text: this._translateService.instant('BUTTONS.CANCEL'),
                        onClick: () => {},
                        color: SharedUiColorNameV2.PRIMARY,
                        buttonStyle: ButtonStyle.OUTLINE
                    }
                ]
            })
            .afterClosed()
            .then();
    }

    private deleteAlertScenario(alertScenarioId: AlertScenarioId): Promise<void> {
        return this._advizeoCoreAlertScenariosProvider
            .deleteAlertScenario(alertScenarioId)
            .then(() => this._alertsScenariosListUpdate$.next());
    }

    setAlertScenarioEnabled(alertScenarioId: AlertScenarioId, enabled: boolean): Promise<void> {
        return this._advizeoCoreAlertScenariosProvider
            .setAlertScenarioEnabled(alertScenarioId, AlertScenarioType.THRESHOLD, enabled)
            .then(() => this._alertsScenariosListUpdate$.next());
    }

    getUnitCodesFromMeasureType$(measureTypeCode: MeasureTypeCode): Observable<UnitCode[]> {
        switch (measureTypeCode) {
            case MeasureTypeCode.AIR_QUALITY:
                return of([UnitCode.PARTS_PER_MILLION, UnitCode.MILLIONS_PER_M3]);
            default:
                const measureTypeToUnitGroupCode: Record<Exclude<MeasureTypeCode, MeasureTypeCode.AIR_QUALITY>, UnitGroupCode> = {
                    [MeasureTypeCode.POWER]: UnitGroupCode.POWER,
                    [MeasureTypeCode.PRESSURE]: UnitGroupCode.PRESSURE,
                    [MeasureTypeCode.TEMPERATURE]: UnitGroupCode.TEMPERATURE,
                    [MeasureTypeCode.FLOW_RATE]: UnitGroupCode.FLOW_RATE,
                    [MeasureTypeCode.HUMIDITY]: UnitGroupCode.HUMIDITY,
                    [MeasureTypeCode.LUMINOSITY]: UnitGroupCode.LUMINOSITY
                };
                return this._unitProvider
                    .getUnitsByGroupCode$(measureTypeToUnitGroupCode[measureTypeCode])
                    .pipe(map(units => units.map(unit => unit.unitCode)));
        }
    }

    extractErrorsFromApiFailure(errorCode: APIError, payload: APIError[]): APIError[] {
        if (errorCode === APIError.INVALID_CONFIGURATION) {
            return payload;
        } else {
            return [errorCode];
        }
    }

    showErrors(errors: APIError[]) {
        if (errors.length === 1) {
            switch (errors[0]) {
                case APIError.ALREADY_USED_NAME:
                    this._informationProvider.showToast(
                        this._translateService.instant('PAGES.ALERT_SCENARIO.ERRORS.ALREADY_USED_NAME'),
                        'error'
                    );
                    break;
                case APIError.INVALID_THRESHOLD:
                    this._informationProvider.showToast(
                        this._translateService.instant('PAGES.ALERT_SCENARIO.ERRORS.INVALID_THRESHOLD'),
                        'error'
                    );
                    break;
                case APIError.INVALID_DATE_RANGE:
                    this._informationProvider.showToast(
                        this._translateService.instant('PAGES.ALERT_SCENARIO.ERRORS.INVALID_DATE_RANGE'),
                        'error'
                    );
                    break;

                default:
                    this._informationProvider.showToast(
                        this._translateService.instant('PAGES.ALERT_SCENARIO.ERRORS.UNKNOWN_SINGLE_ERROR'),
                        'error'
                    );
                    break;
            }
        } else {
            this._informationProvider.showToast(this._translateService.instant('PAGES.ALERT_SCENARIO.ERRORS.MULTIPLE_ERRORS'), 'error');
        }
    }

    getThresholdScenarioAlertsTimeseriesAnalysis$(
        scenarioId: AlertScenarioId,
        timeseriesId: SensorTimeseriesId,
        startDate: UnzonedDateTime,
        endDate: UnzonedDateTime
    ): Observable<ThresholdScenarioAlertsTimeseriesAnalysis> {
        return this._advizeoCoreThresholdScenariosProvider.getThresholdScenarioAlertsTimeseriesAnalysis$(
            scenarioId,
            timeseriesId,
            startDate,
            endDate
        );
    }
}
